import { SPRITE_SIZE } from '../../game/events';
import { ROW } from '../../game/animations';

/**
 * Frame data model and PNG packing for the sprite editor.
 *
 * A frame is a flat array of `SPRITE_SIZE * SPRITE_SIZE` entries, each either
 * a CSS colour string or `null` for transparent. Flat arrays rather than 2D so
 * indexing is one multiply and immutable updates are a single `slice()`.
 */

export const SIZE = SPRITE_SIZE; // 32
export const COLS = 4;
export const ROWS = 4;
export const FRAME_COUNT = COLS * ROWS;
export const SHEET_W = SIZE * COLS;
export const SHEET_H = SIZE * ROWS;

/** Row labels in sheet order — derived from ROW so the two cannot disagree. */
export const ROW_LABELS = Object.entries(ROW)
  .sort((a, b) => a[1] - b[1])
  .map(([facing]) => facing);

/** Column meaning, per docs/sprite-spec.md. */
export const COLUMN_LABELS = ['idle', 'step L', 'spare', 'step R'];

export const emptyFrame = () => new Array(SIZE * SIZE).fill(null);
export const emptySheet = () => Array.from({ length: FRAME_COUNT }, emptyFrame);

export const idx = (x, y) => y * SIZE + x;
export const frameIndex = (col, row) => row * COLS + col;

/** Palette seeded from the placeholder generator, so edits stay coherent. */
export const DEFAULT_PALETTE = [
  '#1a1420', // outline
  '#4a3728', // hair
  '#e8b88a', // skin
  '#c99a6f', // skin shade
  '#d6dae0', // shirt
  '#a8adb8', // shirt shade
  '#3d4a5c', // pants
  '#2b3543', // pants shade
  '#2a2320', // shoe
  '#d4a24c', // accent gold
  '#4cafd4', // accent cyan
  '#ffffff',
];

/**
 * Flood fill from (x, y), replacing the contiguous region of whatever colour
 * is there. Iterative rather than recursive — a 32x32 fill is only 1024 cells,
 * but recursion depth on a full-canvas fill is exactly the kind of thing that
 * blows the stack on a bad day.
 */
export function floodFill(pixels, x, y, color) {
  const target = pixels[idx(x, y)];
  if (target === color) return pixels;

  const next = pixels.slice();
  const stack = [[x, y]];

  while (stack.length) {
    const [cx, cy] = stack.pop();
    const inBounds = cx >= 0 && cy >= 0 && cx < SIZE && cy < SIZE;

    if (inBounds && next[idx(cx, cy)] === target) {
      next[idx(cx, cy)] = color;
      stack.push([cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]);
    }
  }

  return next;
}

/**
 * Pack all frames into a single 128x128 PNG data URL.
 *
 * Uses an offscreen canvas rather than the hand-rolled Node PNG encoder — in
 * the browser, `toDataURL` is already there and correct.
 */
export function framesToDataUrl(frames) {
  const canvas = document.createElement('canvas');
  canvas.width = SHEET_W;
  canvas.height = SHEET_H;
  const ctx = canvas.getContext('2d');

  frames.forEach((pixels, i) => {
    const ox = (i % COLS) * SIZE;
    const oy = Math.floor(i / COLS) * SIZE;

    for (let y = 0; y < SIZE; y += 1) {
      for (let x = 0; x < SIZE; x += 1) {
        const color = pixels[idx(x, y)];
        if (color) {
          ctx.fillStyle = color;
          ctx.fillRect(ox + x, oy + y, 1, 1);
        }
      }
    }
  });

  return canvas.toDataURL('image/png');
}

/**
 * Read an existing sheet back into frame data, so generated placeholder art
 * can be opened and refined rather than redrawn.
 *
 * Rejects sheets that are not the expected size — silently cropping a
 * mismatched sheet would scramble the frames in a way that looks like an
 * editor bug.
 */
export function loadSheet(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      if (img.width !== SHEET_W || img.height !== SHEET_H) {
        reject(new Error(`Expected ${SHEET_W}x${SHEET_H}, got ${img.width}x${img.height}`));
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = SHEET_W;
      canvas.height = SHEET_H;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const { data } = ctx.getImageData(0, 0, SHEET_W, SHEET_H);

      const frames = Array.from({ length: FRAME_COUNT }, (_, i) => {
        const ox = (i % COLS) * SIZE;
        const oy = Math.floor(i / COLS) * SIZE;
        const pixels = emptyFrame();

        for (let y = 0; y < SIZE; y += 1) {
          for (let x = 0; x < SIZE; x += 1) {
            const p = ((oy + y) * SHEET_W + (ox + x)) * 4;
            if (data[p + 3] !== 0) {
              const hex = [data[p], data[p + 1], data[p + 2]]
                .map((c) => c.toString(16).padStart(2, '0'))
                .join('');
              pixels[idx(x, y)] = `#${hex}`;
            }
          }
        }
        return pixels;
      });

      resolve(frames);
    };

    img.onerror = () => reject(new Error(`Could not load ${src}`));
    img.src = src;
  });
}
